/**
 * Cross-field invariants — the checks JSON Schema cannot express. Split from
 * validator.ts so environments that can't run Ajv (Cloudflare Workers forbid
 * `new Function`) can pair this plain code with an Ajv-standalone precompiled
 * structural validator. validator.ts composes both for Node/browser use.
 *
 * Error strings carry JSON paths + expected values — the /generate repair
 * loop feeds them back to the LLM verbatim.
 */

import type { Preset, SegmentedLayer } from "./types.ts";
import { isSegmentedLayer } from "./types.ts";

export function crossFieldErrors(preset: Preset): string[] {
  const errors: string[] = [];
  const duration = preset.meta.durationSec;

  // Unique layer ids — sync, editor selection, and ducking targets rely on them.
  const seen = new Set<string>();
  for (const [i, layer] of preset.layers.entries()) {
    if (seen.has(layer.id)) {
      errors.push(
        `/layers/${i}/id: duplicate layer id "${layer.id}" — layer ids must be unique within a preset`,
      );
    }
    seen.add(layer.id);
  }

  for (const [i, layer] of preset.layers.entries()) {
    const at = `/layers/${i}`;
    if (isSegmentedLayer(layer)) {
      errors.push(...segmentErrors(layer, at, duration));
    } else if (layer.type === "noise" && layer.envelope) {
      let prevT = -Infinity;
      for (const [j, p] of layer.envelope.entries()) {
        if (p.t <= prevT) {
          errors.push(
            `${at}/envelope/${j}/t: envelope times must be strictly increasing (${p.t} follows ${prevT})`,
          );
        }
        if (p.t > duration) {
          errors.push(
            `${at}/envelope/${j}/t: ${p.t} exceeds meta.durationSec (${duration})`,
          );
        }
        prevT = p.t;
      }
    } else if (layer.type === "voice") {
      const start = layer.startSec ?? 0;
      if (start >= duration) {
        errors.push(
          `${at}/startSec: ${start} must be less than meta.durationSec (${duration})`,
        );
      }
    }
  }

  return errors;
}

function segmentErrors(
  layer: SegmentedLayer,
  at: string,
  duration: number,
): string[] {
  const errors: string[] = [];
  const segs = layer.segments;

  for (const [j, seg] of segs.entries()) {
    const sat = `${at}/segments/${j}`;

    if (!(seg.t0 < seg.t1)) {
      errors.push(`${sat}: t0 (${seg.t0}) must be strictly less than t1 (${seg.t1})`);
    }

    // exp interpolation is geometric — it cannot pass through zero.
    if (seg.curve === "exp") {
      for (const [name, pair] of [
        ["carrierHz", seg.carrierHz],
        ["beatHz", seg.beatHz],
      ] as const) {
        if (pair[0] <= 0 || pair[1] <= 0) {
          errors.push(
            `${sat}/${name}: exp curve requires strictly positive endpoints (got [${pair[0]}, ${pair[1]}]) — use "linear" to ramp to/from 0`,
          );
        }
      }
    }

    // Beat-specific physics per technique.
    if (layer.type === "isochronic") {
      if (seg.beatHz[0] <= 0 || seg.beatHz[1] <= 0) {
        errors.push(
          `${sat}/beatHz: isochronic gating frequency must be > 0 (got [${seg.beatHz[0]}, ${seg.beatHz[1]}])`,
        );
      }
    } else {
      // binaural/monaural: both sidebands carrier ± beat/2 must stay positive.
      // Interpolation is monotone between endpoints, so endpoint checks suffice.
      for (const end of [0, 1] as const) {
        const lower = seg.carrierHz[end] - seg.beatHz[end] / 2;
        if (lower <= 0) {
          errors.push(
            `${sat}: carrier − beat/2 must stay > 0 Hz (at ${end === 0 ? "t0" : "t1"}: ${seg.carrierHz[end]} − ${seg.beatHz[end]}/2 = ${lower})`,
          );
        }
      }
    }
  }

  // Coverage + contiguity: segments must tile [0, durationSec] exactly.
  // v1 rule: full coverage, sample-exact boundaries — no gaps, no overlap,
  // nothing undefined between segments. (Relaxing later is compatible;
  // tightening later would not be.)
  const first = segs[0];
  if (first && first.t0 !== 0) {
    errors.push(
      `${at}/segments/0/t0: first segment must start at 0 (got ${first.t0})`,
    );
  }
  for (let j = 1; j < segs.length; j++) {
    const prev = segs[j - 1]!;
    const cur = segs[j]!;
    if (cur.t0 !== prev.t1) {
      errors.push(
        `${at}/segments/${j}/t0: segments must be contiguous — t0 (${cur.t0}) must equal previous segment's t1 (${prev.t1})`,
      );
    }
  }
  const last = segs[segs.length - 1];
  if (last && last.t1 !== duration) {
    errors.push(
      `${at}/segments/${segs.length - 1}/t1: last segment must end at meta.durationSec (${duration}), got ${last.t1}`,
    );
  }

  return errors;
}
